import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import { useLearning } from 'src/hook/data/useLearning';
import { useScenario } from 'src/hook/data/useScenario';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { AnimationType, Pages } from 'src/model/enum/enums';
import { Word } from 'src/model/Word';
import { IconLocation, ScenariosLocation } from 'src/utils/URLUtils';
import styles from './ScenarioDescription.module.scss';
const ScenarioDescription = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('id');
    const index = searchParams.get('index');
    const { learningInfo } = useLearning();
    const { open } = useComponentModal();
    const navigate = useNavigate();
    const { getRandomWords, scenarios, isLoading, scenarioRandomWords } = useScenario();
    const [hasInitialized, setHasInitialized] = useState(false);
    const { showClosePopUp } = usePopUp();

    const handleWordClick = (word: Word) => {
        open(Pages.Definition, { word: word }, AnimationType.SlideUp);
    }

    const handleCloseModal = () => {
        navigate(`/scenario/category?id=${ref}`);
    }

    const handleStart = () => {
        navigate(`/scenario/chat?id=${ref}&index=${index}`);
    }

    useEffect(() => {
        if (!scenarios?.categories || !ref || !index || hasInitialized) return;

        const categoryRef = ref.substring(0, 2);
        const contextRef = ref.substring(2, 4);
        const category = scenarios?.categories.find(cat => cat.ref === categoryRef);
        if (category) {
            const context = category.ctx.find(ctx => ctx.ref === contextRef);
            const wordsList = context?.chl.find(chl => chl.ref === index)?.w;
            if (wordsList) {
                // Get random words and update Redux state
                const randomWords = getRandomWords(wordsList, true);
                // If we got words, mark as initialized
                if (randomWords.length > 0) {
                    setHasInitialized(true);
                }
            }
        }
    }, [scenarios, ref, index, getRandomWords, hasInitialized]);

    if (isLoading || !scenarios || !scenarioRandomWords) {
        return <Spinner fullPage={true} />;
    }

    if (!ref || !index) {
        return <div>{t('Scenario not found')}</div>;
    }

    return (
        <div className={styles.scenarioDescription}>
            <div style={{ backgroundImage: `url(${ScenariosLocation(ref)})` }}
                className={styles.questionImage} />
            <span onClick={() => showClosePopUp(handleCloseModal)}
                className={styles.close}>
                <img src={IconLocation('x')} alt="close" />
            </span>
            <div className={styles.wordsContainer}>
                <h1>{t('Words')}</h1>
                <p>{t('In this challenge, you should correctly use all of the following words:')}</p>
                <div className={styles.wordsList}>
                    {scenarioRandomWords.map((word, index) => (
                        <span onClick={() => handleWordClick(word)}
                            key={index}
                            className={`${styles.wordItem} ${learningInfo?.KnownWordIds
                                .includes(word.ID)
                                ? styles.knownWord
                                : learningInfo?.ToLearn
                                    .includes(word.ID)
                                    ? styles.ToLearnWord
                                    : styles.unknownWord
                                }`}
                        >
                            {word.Text}
                        </span>
                    ))}
                </div>
                <h1>{t('Ready?')}</h1>
                <button className={styles.startButton} onClick={handleStart}>{t('Start')}</button>
            </div>
        </div>
    );
};

export default ScenarioDescription;
